import React, { createContext, useContext, useState, useCallback } from 'react';

export interface Tab {
  id: string;
  name: string;
}

interface PageContextType {
  tabs: Tab[] | undefined;
  activeTab: string | undefined;
  showPageHeader: boolean;
  setTabs: (tabs: Tab[] | undefined) => void;
  setActiveTab: (tabId: string) => void;
  setShowPageHeader: (show: boolean) => void;
}

const PageContext = createContext<PageContextType | undefined>(undefined);

export function PageProvider({ children }: { children: React.ReactNode }) {
  const [tabs, setTabs] = useState<Tab[] | undefined>();
  const [activeTab, setActiveTab] = useState<string | undefined>();
  const [showPageHeader, setShowPageHeader] = useState<boolean>(true);

  const handleSetTabs = useCallback((newTabs: Tab[] | undefined) => {
    setTabs(newTabs);
    // Reset active tab when tabs change
    if (newTabs && newTabs.length > 0 && !newTabs.find(t => t.id === activeTab)) {
      setActiveTab(newTabs[0].id);
    }
  }, [activeTab]);

  return (
    <PageContext.Provider value={{ tabs, activeTab, showPageHeader, setTabs: handleSetTabs, setActiveTab, setShowPageHeader }}>
      {children}
    </PageContext.Provider>
  );
}

export function usePageContext(options?: { showPageHeader?: boolean }) {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error('usePageContext must be used within a PageProvider');
  }
  
  // Set showPageHeader if provided
  React.useEffect(() => {
    if (options?.showPageHeader !== undefined) {
      context.setShowPageHeader(options.showPageHeader);
    }
    // Reset to true on unmount
    return () => context.setShowPageHeader(true);
  }, [options?.showPageHeader, context]);
  
  return context;
}